import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Routify" },
      { name: "description", content: "How Routify collects, uses and protects your personal data under the GDPR." },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/legal" className="text-sm text-muted-foreground hover:text-foreground">← Legal</Link>
      <h1 className="mt-6 text-3xl font-semibold">Privacy Policy</h1>
      <p className="mt-2 text-xs text-muted-foreground">Last updated / Última atualização: 2026-07-19 · Version 2.1</p>

      <section className="mt-8 space-y-5 text-sm leading-relaxed">
        <p>
          This Privacy Policy explains how personal data is processed when you use Routify (the
          "Service"). It is written to comply with Regulation (EU) 2016/679 ("GDPR"), Portuguese
          Law 58/2019 and Law 41/2004 (privacy in electronic communications).
        </p>

        <h2 className="text-lg font-semibold">1. Data controller</h2>
        <p>
          The data controller is the entity identified on the{" "}
          <Link to="/legal" className="underline">Legal page</Link>. Contact for privacy
          matters: <a className="underline" href="mailto:Routify.info@gmail.com">Routify.info@gmail.com</a>.
          We have not appointed a Data Protection Officer (DPO) as we do not meet the criteria
          of Article 37 GDPR.
        </p>

        <h2 className="text-lg font-semibold">2. Categories of personal data</h2>
        <ul className="list-disc pl-5">
          <li><strong>Itinerary inputs</strong>: city, coordinates, duration, interests, free-text notes.</li>
          <li><strong>Approximate location</strong>: only when you actively click "Use my current location" (browser geolocation, with consent).</li>
          <li><strong>Account data</strong>: email, display name, authentication identifiers, password hash (when using email/password) or SSO identifier.</li>
          <li><strong>Subscription &amp; billing data</strong>: billing email, country, VAT ID (if applicable), payment provider customer ID, subscription status, invoices. Full card data is processed exclusively by Stripe.</li>
          <li><strong>Custom planning data</strong>: destination, dates, travellers, preferences, budget and free-text brief you submit; email address for quote and delivery; delivered PDF/route link.</li>
          <li><strong>Partner Programme application data</strong>: name, email, country, social network handles and links (Instagram, TikTok, YouTube), approximate follower count, average post/story views, main audience country and age range, content niche, media kit link, past partnerships, promotion plan, motivation, and the record that you accepted the Partner Programme Terms and this Privacy Policy. If approved, we also process your promo code, referral link, click/conversion metrics, commission balances and the banking details you provide for payouts.</li>
          <li><strong>Usage data</strong>: itinerary generations, saved itineraries, feature usage, quota counters, timestamps.</li>
          <li><strong>Technical data</strong>: IP address, browser/device type, language, referrer, server logs (used briefly for security and debugging).</li>
          <li><strong>Communications</strong>: emails you send us and our replies.</li>
        </ul>

        <h2 className="text-lg font-semibold">3. Purposes and legal bases</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Purpose</th>
                <th className="text-left p-2">Data used</th>
                <th className="text-left p-2">Legal basis (GDPR Art. 6)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="p-2">Generate itineraries</td><td className="p-2">Inputs, account ID</td><td className="p-2">Contract (6(1)(b))</td></tr>
              <tr><td className="p-2">Assess Partner Programme applications and manage the partnership</td><td className="p-2">Application form data, promo/referral metrics, payout details</td><td className="p-2">Pre-contractual measures &amp; contract (6(1)(b)); legal obligation for tax/accounting (6(1)(c)); legitimate interest in fraud prevention (6(1)(f))</td></tr>
              <tr><td className="p-2">Manage your account</td><td className="p-2">Account data</td><td className="p-2">Contract (6(1)(b))</td></tr>
              <tr><td className="p-2">Process subscriptions &amp; payments</td><td className="p-2">Billing data</td><td className="p-2">Contract (6(1)(b)) + legal obligation (6(1)(c))</td></tr>
              <tr><td className="p-2">Send service emails (welcome, receipts, account)</td><td className="p-2">Email</td><td className="p-2">Contract (6(1)(b))</td></tr>
              <tr><td className="p-2">Security, fraud and abuse prevention</td><td className="p-2">Technical data, usage</td><td className="p-2">Legitimate interest (6(1)(f))</td></tr>
              <tr><td className="p-2">Audience measurement (Google Analytics)</td><td className="p-2">Technical data</td><td className="p-2">Consent (6(1)(a))</td></tr>
              <tr><td className="p-2">Approximate geolocation</td><td className="p-2">GPS coordinates</td><td className="p-2">Consent (6(1)(a))</td></tr>
              <tr><td className="p-2">Comply with tax/accounting law</td><td className="p-2">Billing data</td><td className="p-2">Legal obligation (6(1)(c))</td></tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-lg font-semibold">4. Automated decisions and AI processing</h2>
        <p>
          Itineraries are generated by large language models accessed through two providers:
          the <strong>Lovable AI Gateway</strong> (Google Gemini) for narrative and planning tasks,
          and <strong>OpenAI</strong> (GPT models, via direct API) for the classic stop generator.
          The inputs you submit (city, interests, free-text
          notes) are sent to the model to produce the response. We <strong>do not perform
          automated decision-making with legal or similarly significant effects on you</strong>{" "}
          within the meaning of Article 22 GDPR. Outputs are informational suggestions; you
          decide whether to follow them. We do not use your inputs to train AI models, and our
          processors are contractually bound not to use them to train their models when
          processed via our API integrations.
        </p>

        <h2 className="text-lg font-semibold">5. Recipients and processors (subprocessors)</h2>
        <ul className="list-disc pl-5">
          <li><strong>Lovable / Lovable AI Gateway</strong> — hosting of the application and routing of AI requests to underlying model providers (Google Gemini) on our behalf.</li>
          <li><strong>OpenAI, L.L.C.</strong> — generative AI (GPT models) accessed directly via API for classic itinerary generation.</li>
          <li><strong>Google Ireland Ltd.</strong> — Google Maps Platform (maps, places, directions), Google Analytics (audience measurement, with consent) and Gemini (generative AI, via the Lovable AI Gateway).</li>
          <li><strong>Supabase</strong> — authentication, database, file storage (EU region when available), used as the backend of Lovable Cloud.</li>
          <li><strong>Cloudflare, Inc.</strong> — edge runtime, hosting and DDoS protection.</li>
          <li><strong>Stripe Payments Europe, Ltd.</strong> (Ireland) — payment processing for Premium, Pro and custom planning.</li>
          <li><strong>Resend</strong> — transactional email delivery (welcome, receipts, custom planning lifecycle).</li>
        </ul>
        <p className="mt-2">
          Each processor is bound by a data processing agreement (Article 28 GDPR). We do not
          sell your personal data.
        </p>

        <h2 className="text-lg font-semibold">6. International transfers</h2>
        <p>
          Some processors are located outside the EEA (notably in the United States). Transfers
          rely on the EU-U.S. Data Privacy Framework where the recipient is certified, on the
          European Commission's Standard Contractual Clauses (Decision 2021/914), and, where
          appropriate, on additional technical and organisational safeguards (encryption in
          transit and at rest, access controls).
        </p>

        <h2 className="text-lg font-semibold">7. Retention</h2>
        <ul className="list-disc pl-5">
          <li><strong>Itinerary inputs &amp; outputs</strong>: kept for the duration of your session and, if you save them, until you delete them or delete your account.</li>
          <li><strong>Account data</strong>: kept while the account is active; deleted within 30 days of account closure, except where retention is legally required.</li>
          <li><strong>Billing records</strong>: 10 years (Portuguese tax law, art. 123 CIRC and art. 44 CIVA).</li>
          <li><strong>Server &amp; security logs</strong>: up to 90 days.</li>
          <li><strong>Email suppression lists</strong>: kept indefinitely to honour unsubscribe choices.</li>
          <li><strong>Partner Programme applications</strong>: kept for up to 24 months from submission for rejected candidates (to avoid duplicated resubmissions and for fraud prevention), and for the duration of the partnership plus 10 years for accounting purposes when a partnership is approved.</li>
        </ul>

        <h2 className="text-lg font-semibold">8. Security</h2>
        <p>
          We apply appropriate technical and organisational measures (encryption in transit via
          TLS, encryption at rest, role-based access, principle of least privilege, row-level
          security on the database, secure secret management, periodic backups). No system is
          100% secure; we cannot guarantee absolute security but commit to notifying you and the
          CNPD of any personal data breach as required by Articles 33-34 GDPR.
        </p>

        <h2 className="text-lg font-semibold">9. Your rights</h2>
        <p>Under the GDPR you have the right to:</p>
        <ul className="list-disc pl-5">
          <li><strong>Access</strong> a copy of your personal data (Art. 15);</li>
          <li><strong>Rectify</strong> inaccurate data (Art. 16);</li>
          <li><strong>Erase</strong> your data ("right to be forgotten") (Art. 17);</li>
          <li><strong>Restrict</strong> processing (Art. 18);</li>
          <li><strong>Data portability</strong> in a structured, machine-readable format (Art. 20);</li>
          <li><strong>Object</strong> to processing based on legitimate interest (Art. 21);</li>
          <li><strong>Withdraw consent</strong> at any time, without affecting prior lawful processing (Art. 7);</li>
          <li>Not be subject to solely automated decisions with significant effects (Art. 22).</li>
        </ul>
        <p>
          To exercise these rights, email{" "}
          <a className="underline" href="mailto:Routify.info@gmail.com">Routify.info@gmail.com</a>.
          We respond within 30 days (extendable up to 60 days for complex requests). We may ask
          for proof of identity to prevent unauthorised disclosure.
        </p>

        <h2 className="text-lg font-semibold">10. Right to lodge a complaint</h2>
        <p>
          You have the right to lodge a complaint with the Portuguese supervisory authority{" "}
          <em>Comissão Nacional de Proteção de Dados (CNPD)</em>:{" "}
          <a className="underline" href="https://www.cnpd.pt" target="_blank" rel="noreferrer">cnpd.pt</a>{" "}
          — Av. D. Carlos I, n.º 134, 1.º, 1200-651 Lisboa. You may also complain to the
          supervisory authority of your habitual residence.
        </p>

        <h2 className="text-lg font-semibold">11. Children</h2>
        <p>
          The Service is not directed at children under 16. We do not knowingly collect data
          from children under 16 without parental consent. If you believe a child has provided
          personal data to us, please contact us and we will delete it.
        </p>

        <h2 className="text-lg font-semibold">12. Cookies</h2>
        <p>
          For detailed information about cookies and local storage, see our{" "}
          <Link to="/cookies" className="underline">Cookie Policy</Link>.
        </p>

        <h2 className="text-lg font-semibold">13. Changes to this Policy</h2>
        <p>
          We may update this Policy. The "Last updated" date above indicates the current
          version. Material changes will be notified by email or in-app.
        </p>

        <hr className="my-8 border-border" />

        <h2 className="text-xl font-semibold">Versão em Português</h2>

        <p className="mt-4">
          A presente Política de Privacidade descreve como são tratados os dados pessoais no
          âmbito do Serviço Routify, em cumprimento do RGPD, da Lei n.º 58/2019 e da Lei n.º
          41/2004.
        </p>

        <h3 className="text-base font-semibold mt-4">1. Responsável pelo tratamento</h3>
        <p>
          Identificado na página <Link to="/legal" className="underline">Legal</Link>. Contacto
          para questões de privacidade: <a className="underline" href="mailto:Routify.info@gmail.com">Routify.info@gmail.com</a>.
          Não há obrigação legal de designação de EPD/DPO (art. 37.º RGPD).
        </p>

        <h3 className="text-base font-semibold mt-4">2. Dados tratados</h3>
        <p>
          Inputs do itinerário; localização aproximada (com consentimento); dados de conta
          (email, nome, identificador de autenticação); dados de subscrição e faturação;
          utilização (rotas geradas, contadores de quota); dados técnicos (IP, navegador, logs);
          comunicações. No âmbito do <strong>Programa de Parceiros</strong> tratamos ainda os
          dados submetidos no formulário de candidatura (nome, email, país, redes sociais e
          respetivos links, nº aproximado de seguidores, médias de visualizações, país e faixa
          etária principais da audiência, nicho, media kit, parcerias anteriores, plano de
          divulgação, motivação e o registo de aceitação dos Termos do Programa e da presente
          Política) e, em caso de aprovação, código promocional, link de referência, métricas de
          cliques e conversões, saldos de comissões e dados bancários para pagamento.
        </p>

        <h3 className="text-base font-semibold mt-4">3. Finalidades e fundamentos</h3>
        <p>
          Execução do contrato (geração de itinerários, conta, subscrição); obrigações legais
          (faturação e fiscalidade); interesse legítimo (segurança e prevenção de abuso);
          consentimento (geolocalização, Google Analytics).
        </p>

        <h3 className="text-base font-semibold mt-4">4. IA e decisões automatizadas</h3>
        <p>
          Os itinerários são gerados por modelos de linguagem acedidos através de dois
          fornecedores: a <strong>Lovable AI Gateway</strong> (Google Gemini) para narrativa e
          planeamento, e a <strong>OpenAI</strong> (modelos GPT, via API direta) para o gerador
          clássico de pontos. Não há decisões automatizadas com efeitos jurídicos ou
          significativos na esfera do utilizador (art. 22.º RGPD). Os inputs não
          são utilizados para treinar modelos.
        </p>

        <h3 className="text-base font-semibold mt-4">5. Subcontratantes</h3>
        <p>
          Lovable / Lovable AI Gateway, OpenAI (modelos GPT via API direta), Google (Maps +
          Analytics + Gemini), Supabase (backend do Lovable Cloud), Cloudflare, Stripe e Resend
          (email transacional). Todos vinculados
          por acordo de tratamento (art. 28.º RGPD). Não vendemos dados pessoais.
        </p>

        <h3 className="text-base font-semibold mt-4">6. Transferências internacionais</h3>
        <p>
          Alguns subcontratantes estão fora do EEE (sobretudo EUA). Transferências baseadas no
          EU-U.S. Data Privacy Framework, em Cláusulas Contratuais-Tipo (Decisão 2021/914) e em
          medidas técnicas adicionais (cifragem em trânsito e em repouso, controlos de acesso).
        </p>

        <h3 className="text-base font-semibold mt-4">7. Conservação</h3>
        <ul className="list-disc pl-5">
          <li>Itinerários: durante a sessão ou até o utilizador apagar/encerrar a conta;</li>
          <li>Conta: enquanto ativa; eliminada em 30 dias após encerramento, salvo retenção legal;</li>
          <li>Faturação: 10 anos (arts. 123.º CIRC e 44.º CIVA);</li>
          <li>Logs de segurança: até 90 dias;</li>
          <li>Lista de supressão de email: indefinidamente para respeitar os pedidos de cancelamento.</li>
        </ul>

        <h3 className="text-base font-semibold mt-4">8. Segurança</h3>
        <p>
          TLS, cifragem em repouso, controlo de acessos, princípio do mínimo privilégio, RLS na
          base de dados, gestão segura de segredos, backups. Compromisso de notificação de
          violações nos termos dos arts. 33.º e 34.º RGPD.
        </p>

        <h3 className="text-base font-semibold mt-4">9. Direitos do titular</h3>
        <p>
          Acesso, retificação, apagamento, limitação, portabilidade, oposição, retirada de
          consentimento e direito de não ser sujeito a decisões automatizadas. Pedidos por email
          para Routify.info@gmail.com, com resposta em 30 dias (prorrogáveis até 60).
        </p>

        <h3 className="text-base font-semibold mt-4">10. Reclamação</h3>
        <p>
          Pode reclamar junto da CNPD (cnpd.pt) — Av. D. Carlos I, n.º 134, 1.º, 1200-651
          Lisboa, ou da autoridade de controlo da sua residência habitual.
        </p>

        <h3 className="text-base font-semibold mt-4">11. Menores</h3>
        <p>
          Serviço não destinado a menores de 16 anos. Não recolhemos conscientemente dados de
          menores sem consentimento parental.
        </p>

        <h3 className="text-base font-semibold mt-4">12. Cookies</h3>
        <p>
          Ver a <Link to="/cookies" className="underline">Política de Cookies</Link>.
        </p>

        <h3 className="text-base font-semibold mt-4">13. Alterações</h3>
        <p>
          Esta Política pode ser atualizada; a data acima reflete a versão em vigor. Alterações
          materiais serão notificadas.
        </p>
      </section>
    </main>
  );
}
